import { getTaskFilter, taskFilterMarkup } from '../../ui/note-task-filter.js';
import { getMobileNoteResults } from '../notes-search.js';
import { highlightText } from '../../../../engine/core/text-search.js';
import { renderMarkdown } from "../../views/notes/markdown.js";
import { renderMobileToday } from "./today.js";
import { cameraIcon, closeIcon, paperclipIcon, refnoteIcon, searchIcon, trashIcon } from "../icons.js";
import { formatShortDate, formatShortDateTime } from "../utils/dates.js";
import { escapeAttr, escapeHtml, escapeTextarea } from "../utils/html.js";
import { renderEmpty } from "./empty.js";
import { isSyncAvailable } from "../../../../engine/storage/index.js";
import { attachmentsOnDevice } from "../../../../engine/services/attachment-service.js";
import { t } from "../../../../shared/i18n/index.js";

/**
 * Read a note's body.
 *
 * In yxml-v1 (collab) mode the Y.XmlFragment is the source of truth.
 * Empty fragment → empty content, even when the legacy `note.content`
 * string still holds an old value. The legacy string isn't kept in
 * sync with fragment edits in Day 5+, so falling through to it would
 * silently undo intentional clears made on a peer device (or in this
 * same device's prior editor session). The TipTap editor that mounts
 * here binds via ySyncPlugin and already reflects the fragment, so a
 * strict reader keeps the static view consistent with the editor.
 *
 * Legacy (pre-migration) docs still read from the array string.
 */
function readNoteContentMobile(note, callbacks, docId = null) {
  if (!note) return "";
  const sc = callbacks?.sharedCallbacks;
  if (sc?.isCollabDocNotesEnabled?.(docId) && sc?.serializeDocNote && note.id) {
    try {
      return sc.serializeDocNote(note.id, docId) || "";
    } catch {
      // Last-resort fall-through on serializer error.
    }
  }
  return note.content || "";
}

export function renderNotes(appState, deps) {
  const {
    callbacks,
    uiState,
    getActiveDocIdForNotes,
    getNoteEditDraft,
    stashUnsavedNoteEdit,
  } = deps;
  const query = uiState.notesSearchQuery.trim().toLowerCase();
  const task = getTaskFilter(callbacks.sharedCallbacks);
  const searching = !!query || !!task;
  const { groups, matches: searchMatches } = getMobileNoteResults(callbacks, uiState);
  const allNotes = (callbacks.sharedCallbacks.getDocNotes?.() || [])
    .slice()
    .sort((a, b) => new Date(b.updated || b.created || 0) - new Date(a.updated || a.created || 0));
  const notes = searching ? groups.map(group => ({...group.note, _searchDocId: group.docId,
    _searchReason: group.reason, _searchDocTitle: group.docTitle})) : allNotes;


  if (!uiState.activeNoteId && allNotes.length) {
    uiState.activeNoteId = allNotes[0].id;
  }

  const visibleNotes = notes;
  const editingCtx = uiState.noteEditing ? uiState.editingNoteContext : null;
  let activeNote = null;
  if (editingCtx) {
    activeNote = allNotes.find((note) => note.id === editingCtx.noteId) || null;
    if (!activeNote && editingCtx.docId === getActiveDocIdForNotes()) {
      const draft = getNoteEditDraft(editingCtx);
      const base = editingCtx.baseline || {};
      // If sync removes the note while the editor is open, keep rendering the
      // draft against the original note id. Save will fail closed and stash
      // the text instead of showing the draft under a different note.
      activeNote = {
        id: editingCtx.noteId,
        title: draft?.title ?? base.title ?? "",
        content: draft?.content ?? base.content ?? "",
        created: "",
        updated: "",
        refnotes: [],
      };
    }
    if (activeNote && uiState.activeNoteId !== activeNote.id) {
      uiState.activeNoteId = activeNote.id;
    }
  } else {
    activeNote = visibleNotes.find((note) => note.id === uiState.activeNoteId && (note._searchDocId || null) === (uiState.activeNoteDocId || null)) || visibleNotes[0] || null;
  }
  // Keep the selected note id aligned with the note actually rendered below.
  // Otherwise search filtering can display note B while actions still operate
  // on stale note A, which is especially dangerous for edit/delete.
  if (activeNote && !uiState.noteEditing) {
    uiState.activeNoteDocId = activeNote._searchDocId || null;
    uiState.activeNoteId = activeNote.id;
  }

  if (task && !query) uiState.noteSearchMatch = searchMatches.find(match =>
    match.noteId === activeNote?.id && match.docId === (activeNote?._searchDocId || null))?.key || null;
  const matchIndex = searchMatches.findIndex(match => match.key === uiState.noteSearchMatch);

  return `
    <section class="mobile-view mobile-notes-view">
      <div class="mobile-notes-search-row">
        <label class="mobile-search-field mobile-notes-search">
          <span>${searchIcon()}</span>
          <input id="mobile-note-search" type="search" autocomplete="off" aria-label="${escapeAttr(t("views.notesSearch.inputLabel"))}" value="${escapeAttr(uiState.notesSearchQuery)}"
                 data-mobile-action="note-search"
                 placeholder="${escapeAttr(task ? t("views.notesSearch.relatedPlaceholder") : t("views.notesSearch.placeholder"))}" />
        </label>
        ${searching ? `<div class="search-navigation mobile-note-search-navigation"><span data-search-status role="status">${searchMatches.length ? `${matchIndex + 1 || '–'} / ${searchMatches.length}` : escapeHtml(t("views.search.noMatches"))}</span><button type="button" class="search-icon-button" data-note-find="-1" aria-label="${escapeAttr(t("views.notesSearch.prevLabel"))}" title="${escapeAttr(t("views.notesSearch.prev"))}" ${searchMatches.length ? '' : 'disabled'}>‹</button><button type="button" class="search-icon-button" data-note-find="1" aria-label="${escapeAttr(t("views.notesSearch.nextLabel"))}" title="${escapeAttr(t("views.notesSearch.next"))}" ${searchMatches.length ? '' : 'disabled'}>›</button></div>` : ''}
        ${taskFilterMarkup(task, groups.length)}
      </div>
      <div class="mobile-note-list">
        ${notes.length ? notes.map((note) => renderNoteRow(note, uiState)).join("") : renderEmpty(task ? (query ? t("mobile.notes.noRelatedMatch") : t("mobile.notes.noLinked")) : query ? t("mobile.notes.noMatch") : t("mobile.notes.empty"))}
      </div>
      <div class="mobile-note-reader">
        ${activeNote ? renderActiveNote(activeNote, deps) : ""}
      </div>
    </section>
  `;
}

function renderNoteRow(note, uiState) {
  const title = note.title || formatShortDateTime(note.created) || t("mobile.notes.untitled");
  return `
    <button class="mobile-note-row ${note.id === uiState.activeNoteId && (note._searchDocId || null) === (uiState.activeNoteDocId || null) ? "active" : ""}"
            data-mobile-action="select-note"
            data-note-id="${escapeAttr(note.id)}" data-note-doc-id="${escapeAttr(note._searchDocId || '')}">
      <span class="mobile-note-title">${highlightText(title,uiState.notesSearchQuery)}</span>
      <span class="mobile-note-date">${escapeHtml(formatShortDate(note.created))}</span>
      ${note._searchReason ? `<span class="mobile-note-relation">${escapeHtml(note._searchReason)}${note._searchDocId ? ` · ${escapeHtml(note._searchDocTitle)}` : ''}</span>` : ''}
    </button>
  `;
}

function renderActiveNote(note, deps) {
  const {
    callbacks,
    uiState,
    getNoteEditDraft,
    stashUnsavedNoteEdit,
  } = deps;
  const docId = note._searchDocId || null;
  const readOnly = !!docId || callbacks.Store.isReadOnly?.();
  if (uiState.noteEditing && !readOnly) {
    // Collaborative mode: bind to the note's Y.XmlFragment via TipTap +
    // ySyncPlugin so mobile + desktop simultaneous edits char-merge
    // (fragment is the source of truth, no clear-and-rebuild on save).
    // The mount div below is empty here; the actual TipTap editor is
    // installed by interfaces/classic/mobile/index.js's post-render hook and survives
    // re-renders via the same detach/reattach pattern desktop preview
    // uses. Falls back to the textarea path for unmigrated docs.
    const collab = !!callbacks?.sharedCallbacks?.isCollabDocNotesEnabled?.();
    let titleVal = note.title || "";
    let contentVal = readNoteContentMobile(note, callbacks);
    const draft = getNoteEditDraft();
    if (draft) {
      if (draft.title != null) titleVal = draft.title;
      if (draft.content != null) contentVal = draft.content;
    }
    const r = uiState.recoveredEditPayload;
    if (r?.kind === "note") {
      const ctx = uiState.editingNoteContext;
      if (ctx && r.docId === ctx.docId && r.noteId === ctx.noteId && ctx.noteId === note.id) {
        if (r.title != null) titleVal = r.title;
        if (r.content != null) contentVal = r.content;
        uiState.noteEditDraft = {
          docId: ctx.docId,
          noteId: ctx.noteId,
          title: titleVal,
          content: contentVal,
        };
        uiState.recoveredEditPayload = null;
      } else {
        // Doc/note-switch raced between confirm-tap and this render. The
        // stash was already consumed; re-stash so the recovered text is not
        // lost forever.
        stashUnsavedNoteEdit(r.docId, r.noteId, {
          title: r.title, content: r.content, updated: r.updated,
        });
        uiState.recoveredEditPayload = null;
      }
    }
    return `
      <div class="mobile-panel-backdrop" data-mobile-action="cancel-note-edit"></div>
      <section role="dialog" aria-modal="true" class="mobile-panel mobile-note-edit-panel mobile-panel-fullscreen">
        <header class="mobile-panel-header">
          <button class="mobile-icon-btn" data-mobile-action="cancel-note-edit" aria-label="${escapeAttr(collab ? t("common.done") : t("common.close"))}">${closeIcon()}</button>
          <div class="mobile-panel-title">${escapeHtml(t("mobile.notes.editTitle"))}</div>
          <button class="mobile-primary-btn compact" data-mobile-action="save-note" data-note-id="${escapeAttr(note.id)}">${escapeHtml(collab ? t("common.done") : t("common.save"))}</button>
        </header>
        <div class="mobile-panel-body mobile-note-editor">
          <input id="mobile-note-title" class="mobile-input-control" placeholder="${escapeAttr(t("common.title"))}" value="${escapeAttr(titleVal)}" />
${isSyncAvailable() || attachmentsOnDevice(deps.callbacks?.Store?.get?.("currentDocId")) ? `
          <div class="mobile-attach-bar">
            <button class="mobile-secondary-btn compact" data-mobile-action="attach-photo"
                    aria-label="${escapeAttr(t("mobile.notes.attachPhotoAria"))}">${cameraIcon()}<span>${escapeHtml(t("mobile.notes.photo"))}</span></button>
            <button class="mobile-secondary-btn compact" data-mobile-action="attach-file"
                    aria-label="${escapeAttr(t("mobile.notes.attachFileAria"))}">${paperclipIcon()}<span>${escapeHtml(t("mobile.notes.file"))}</span></button>
          </div>` : ""}
          ${collab
            ? `<div id="mobile-note-content-mount" class="mobile-note-editor-mount" data-note-id="${escapeAttr(note.id)}"></div>`
            : `<textarea id="mobile-note-content" class="mobile-input-control mobile-note-textarea">${escapeTextarea(contentVal)}</textarea>`
          }
          <div class="mobile-danger-zone">
            <button class="mobile-danger-icon-btn"
                    data-mobile-action="delete-note"
                    data-note-id="${escapeAttr(note.id)}"
                    aria-label="${escapeAttr(t("views.notes.delete"))}"
                    title="${escapeAttr(t("views.notes.delete"))}">${trashIcon()}</button>
          </div>
        </div>
      </section>
    `;
  }

  // Collab path: read HTML directly from the Y.XmlFragment so trailing
  // hardBreaks and empty paragraphs survive (markdown can't encode them).
  // Legacy notes still go through the regex-based renderMarkdown.
  const sc = callbacks?.sharedCallbacks;
  const collab = !!sc?.isCollabDocNotesEnabled?.(docId);
  let rendered = null;
  if (collab && sc?.serializeDocNoteHtml && note.id) {
    try {
      rendered = sc.serializeDocNoteHtml(note.id, docId);
    } catch {
      // Fall through to markdown path.
    }
  }
  if (rendered === null) {
    const noteContent = readNoteContentMobile(note, callbacks, docId);
    rendered = noteContent ? renderMarkdown(String(noteContent), escapeHtml) : '';
  }
  const html = rendered && rendered.trim()
    ? rendered
    : `<p class="mobile-muted">${escapeHtml(t("mobile.notes.noContent"))}</p>`;

  return `
    <article class="mobile-note-article">
      <header class="mobile-note-article-header">
        <div>
          <h2>${escapeHtml(note.title || t("mobile.notes.untitled"))}</h2>
          <span>${escapeHtml(formatShortDateTime(note.updated || note.created))}</span>
        </div>
        ${docId ? `<button class="mobile-text-btn" data-mobile-action="open-doc" data-doc-id="${escapeAttr(docId)}">${escapeHtml(t("mobile.notes.openMap"))}</button>` : readOnly ? "" : `<button class="mobile-text-btn" data-mobile-action="edit-note">${escapeHtml(t("common.edit"))}</button>`}
      </header>
      <div class="mobile-notes-rendered">${html}</div>
      ${(!docId && renderMobileToday(note, deps)) || renderMobileRefnotes(note, deps)}
    </article>
  `;
}

function renderMobileRefnotes(note, { callbacks }) {
  const refnotes = note.refnotes || [];
  if (!refnotes.length) return "";
  const currentDocId = note._searchDocId || callbacks.VaultStorage?.getCurrentDocId?.() || callbacks.Store.get("currentDocId") || null;
  return `
    <div class="mobile-refnote-row">
      ${refnotes.map((refnote) => {
        const linkedDocId = refnote.linkedDocId || "";
        const sameDoc = !linkedDocId || linkedDocId === currentDocId;
        const title = `${!sameDoc && refnote.linkedDocTitle ? `${refnote.linkedDocTitle}::` : ""}${refnote.nodeTitle || t("common.untitled")}`;
        return `
          <button class="mobile-refnote-chip"
                  data-mobile-action="open-refnote"
                  data-node-id="${escapeAttr(refnote.nodeId)}"
                  data-linked-doc-id="${escapeAttr(linkedDocId)}">
            ${refnoteIcon()}<span>${escapeHtml(title)}</span>
          </button>
        `;
      }).join("")}
    </div>
  `;
}
